// Sprint 8 T43 — rediseno OfertaMenu por OPCIONES.
//
// Flujo:
//   1. La comida llega con N opciones (Menu 1, Menu 2, Menu 3) cada una
//      con sus lineas (platos).
//   2. Si hay multiples opciones: dropdown selector. El auxiliar pregunta
//      al paciente "¿prefiere Menu 1, 2 o 3?" y elige una.
//   3. Backend marca las lineas de las OTRAS opciones como excluidas
//      automaticamente (motivo: "No elegida por paciente").
//   4. La UI muestra solo las lineas de la opcion elegida -> el paciente
//      decide si quita platos puntuales o acepta el menu completo.
//   5. Enviar a cocina como antes.
//
// Si solo hay 1 opcion (o ninguna agrupada por compat): flujo legacy igual.
import { useEffect, useMemo, useState } from 'react'
import {
  auxiliarApi, postWithOutboxFallback,
  sustitucionesApi, type SustitucionRanked,
} from '../lib/api'
import { useToast } from './Toast'
import { useConfirm } from './ConfirmDialog'
import { ModalShell } from './ui/ModalShell'
import { alergenosBloqueantes, usePacienteActualStore } from '../stores/pacienteActualStore'
import ProtocoloStepper from './ProtocoloStepper'

interface Linea {
  id: number
  plato_id?: number
  plato_nombre?: string
  cantidad?: string
  ingredientes?: string[]
  excluido_paciente: boolean
  // S8 T43
  menu_opcion_id?: number | null
  menu_opcion_nombre?: string | null
  // Tanda 1 (5-jul): señal AUTORITATIVA del backend (cruce receta×restricción),
  // reemplaza la heurística por texto del nombre del plato.
  conflicto_alergia?: boolean
  conflicto_alergia_alimentos?: string[]
}

interface Comida {
  id: number
  paciente_id?: number
  cliente_nombre?: string
  habitacion?: string
  dieta_nombre?: string
  tiempo_nombre?: string
  lineas: Linea[]
  notas_paciente?: string
}

interface Props {
  comida: Comida
  onClose: () => void
}

export default function OfertaMenu({ comida, onClose }: Props) {
  const { showToast } = useToast()
  const confirm = useConfirm()
  const [lineas, setLineas] = useState<Linea[]>(comida.lineas)
  const [nota, setNota] = useState(comida.notas_paciente || '')
  const [loading, setLoading] = useState(false)
  const [aviso, setAviso] = useState<string | null>(null)
  const [eleccionConfirmadaBackend, setEleccionConfirmadaBackend] = useState<number | null>(null)
  // Fase E: sustitutos del plato seleccionado
  const [sustitutosDe, setSustitutosDe] = useState<Linea | null>(null)

  const alergias = usePacienteActualStore((s) => s.alergiasPacienteActual)
  const bloqueantes = useMemo(() => alergenosBloqueantes(alergias), [alergias])

  // S8 T43: agrupar lineas por opcion + detectar opcion ya elegida
  const opcionesDisponibles = useMemo(() => {
    const m = new Map<number, { nombre: string; lineas: Linea[]; todasExcluidas: boolean }>()
    for (const l of lineas) {
      if (l.menu_opcion_id == null) continue // legacy: lineas sin opcion no entran al selector
      const cur = m.get(l.menu_opcion_id) ?? {
        nombre: l.menu_opcion_nombre ?? `Opción ${l.menu_opcion_id}`,
        lineas: [],
        todasExcluidas: true,
      }
      cur.lineas.push(l)
      if (!l.excluido_paciente) cur.todasExcluidas = false
      m.set(l.menu_opcion_id, cur)
    }
    return m
  }, [lineas])

  // Si hay 1+ opciones, la "elegida" es la que NO esta toda excluida.
  // Si todas las opciones tienen al menos 1 plato no-excluido (caso inicial
  // antes de cualquier seleccion), tomamos la primera como default.
  const opcionElegidaId = useMemo<number | null>(() => {
    const noTodasExcluidas: number[] = []
    for (const [opId, info] of opcionesDisponibles.entries()) {
      if (!info.todasExcluidas) noTodasExcluidas.push(opId)
    }
    if (noTodasExcluidas.length === 1) return noTodasExcluidas[0]
    if (noTodasExcluidas.length === 0) return null
    // multiples opciones con lineas activas = aun no se eligio una explicitamente
    return noTodasExcluidas[0] ?? null
  }, [opcionesDisponibles])

  const lineasSueltas = useMemo(() => lineas.filter((l) => l.menu_opcion_id == null), [lineas])
  const tieneVariasOpciones = opcionesDisponibles.size >= 2

  function alergenoQueBloquea(linea: Linea): string | null {
    // Tanda 1: prioridad a la señal del backend (cruce real receta×restricción,
    // incluye contaminación cruzada de anafilácticas). La heurística por texto
    // queda solo como respaldo cuando el plato aún no tiene mapa de alérgenos.
    if (linea.conflicto_alergia && (linea.conflicto_alergia_alimentos?.length ?? 0) > 0) {
      return linea.conflicto_alergia_alimentos!.join(', ')
    }
    if (bloqueantes.length === 0) return null
    const haystack = [linea.plato_nombre ?? '', ...(linea.ingredientes ?? [])].join(' ').toLowerCase()
    for (const a of bloqueantes) {
      if (a && haystack.includes(a.toLowerCase())) return a
    }
    return null
  }

  async function elegirOpcion(opId: number) {
    if (opId === eleccionConfirmadaBackend) return // ya confirmada en backend
    setLoading(true)
    setAviso(null)
    try {
      await auxiliarApi.elegirOpcion(comida.id, opId)
      setEleccionConfirmadaBackend(opId)
      // Refresca lineas localmente segun la nueva opcion
      setLineas((prev) =>
        prev.map((l) => {
          if (l.menu_opcion_id == null) return l
          if (l.menu_opcion_id === opId) {
            // Restaurar si estaba excluido por otra opcion (no por paciente puntual)
            return { ...l, excluido_paciente: false }
          }
          return { ...l, excluido_paciente: true }
        }),
      )
      showToast(`Menú elegido: ${opcionesDisponibles.get(opId)?.nombre}`, 'success')
    } catch (err: any) {
      showToast(`Error al elegir opción: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  async function toggleExcluir(linea: Linea) {
    if (linea.excluido_paciente) return
    if (alergenoQueBloquea(linea)) return
    const ok = await confirm({
      title: 'Excluir plato',
      message: `¿El paciente NO quiere "${linea.plato_nombre}"?`,
      confirmText: 'Sí, excluir',
      variant: 'danger',
    })
    if (!ok) return
    setLoading(true)
    try {
      const res = await postWithOutboxFallback(
        `/auxiliar/comida/${comida.id}/excluir-linea`,
        { linea_id: linea.id, motivo: 'Excluido por paciente' },
        { comida_id: comida.id, tipo_evento: 'EXCLUSION_LINEA' },
      )
      setLineas(lineas.map((l) => (l.id === linea.id ? { ...l, excluido_paciente: true } : l)))
      if (res.status === 'queued') {
        setAviso('Sin red — guardado offline. Se sincroniza al volver conexión.')
      }
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  async function enviar() {
    setLoading(true)
    setAviso(null)
    try {
      // Si hay varias opciones y no se ha confirmado eleccion al backend,
      // confirmar la opcion default actual antes de enviar a cocina. Sin esto,
      // las lineas de OTRAS opciones quedan visibles para la cocinera.
      if (
        tieneVariasOpciones &&
        opcionElegidaId != null &&
        eleccionConfirmadaBackend !== opcionElegidaId
      ) {
        try {
          await auxiliarApi.elegirOpcion(comida.id, opcionElegidaId)
          setEleccionConfirmadaBackend(opcionElegidaId)
        } catch (err: any) {
          showToast(
            `Error confirmando opción: ${err.response?.data?.detail || err.message}`,
            'error',
          )
          setLoading(false)
          return
        }
      }

      let alguienEnQueue = false
      if (nota.trim()) {
        const r = await postWithOutboxFallback(
          `/auxiliar/comida/${comida.id}/nota-paciente`,
          { notas: nota.trim() },
          { comida_id: comida.id, tipo_evento: 'NOTA_PACIENTE' },
        )
        if (r.status === 'queued') alguienEnQueue = true
      }
      const r2 = await postWithOutboxFallback(
        `/auxiliar/comida/${comida.id}/enviar-cocina`,
        {},
        { comida_id: comida.id, tipo_evento: 'OFERTA_CONFIRMADA' },
      )
      if (r2.status === 'queued') alguienEnQueue = true

      if (alguienEnQueue) {
        showToast(
          'Sin red — la oferta se guardó offline y se sincroniza cuando vuelva conexión.',
          'warning',
        )
      }
      onClose()
    } catch (err: any) {
      showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error')
    } finally {
      setLoading(false)
    }
  }

  // Lineas a mostrar = elegidas + legacy sueltas
  const lineasAMostrar = useMemo(() => {
    const out: Linea[] = []
    if (opcionElegidaId != null) {
      const info = opcionesDisponibles.get(opcionElegidaId)
      if (info) out.push(...info.lineas)
    }
    out.push(...lineasSueltas)
    return out
  }, [opcionElegidaId, opcionesDisponibles, lineasSueltas])

  const opcionElegidaInfo = opcionElegidaId != null ? opcionesDisponibles.get(opcionElegidaId) : null

  // Paso 4 (Confirmar) cuando el usuario tipea una nota o ya excluyo algun
  // plato => indica que esta listo para enviar. Paso 3 mientras esta eligiendo.
  const pasoActual: 3 | 4 = lineas.some((l) => l.excluido_paciente) || nota.trim().length > 0 ? 4 : 3

  return (
    <>
      <ModalShell
        title={<>Hab. {comida.habitacion}</>}
        subtitle={<>{comida.cliente_nombre} · {comida.dieta_nombre} · {comida.tiempo_nombre}</>}
        onClose={onClose}
        closeDisabled={loading}
        maxWidth={560}
      >
        <ProtocoloStepper pasoActual={pasoActual} />

        {/* Selector de opcion - solo si hay 2+ */}
        {tieneVariasOpciones && (
          <div className="mb-4 border border-blue-200 bg-blue-50 rounded p-3">
            <p className="text-sm font-semibold mb-2 text-blue-900">
              🍽️ Hay {opcionesDisponibles.size} menús disponibles. Preguntar al paciente cuál prefiere:
            </p>
            <div className="space-y-2">
              {Array.from(opcionesDisponibles.entries()).map(([opId, info]) => (
                <button
                  key={opId}
                  onClick={() => elegirOpcion(opId)}
                  disabled={loading}
                  className={`w-full text-left p-2 rounded border text-sm transition ${
                    opId === opcionElegidaId
                      ? 'bg-avante-primary text-white border-avante-primary font-semibold'
                      : 'bg-white border-gray-300 hover:bg-blue-100'
                  } disabled:opacity-50`}
                >
                  <div className="font-semibold">
                    {opId === opcionElegidaId ? '✓ ' : ''}{info.nombre}
                  </div>
                  <div className={`text-xs ${opId === opcionElegidaId ? 'text-blue-100' : 'text-gray-600'}`}>
                    {info.lineas.map((l) => l.plato_nombre).filter(Boolean).join(' · ')}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <p className="text-sm font-semibold mb-2">
          Platos a ofrecer{opcionElegidaInfo ? ` — ${opcionElegidaInfo.nombre}` : ''}:
        </p>
        <ul className="space-y-2 mb-4">
          {lineasAMostrar.map((l) => {
            const bloqueoPor = alergenoQueBloquea(l)
            const bloqueado = Boolean(bloqueoPor)
            return (
              <li
                key={l.id}
                className={`p-3 rounded border ${
                  bloqueado
                    ? 'bg-red-50 border-red-300 line-through text-red-700'
                    : l.excluido_paciente
                      ? 'bg-gray-100 line-through text-gray-400'
                      : 'bg-white'
                }`}
              >
                <div className="flex justify-between items-center">
                  <span className="text-sm">
                    {l.cantidad ? `${l.cantidad} ` : ''}{l.plato_nombre}
                  </span>
                  {bloqueado ? (
                    <span
                      role="status"
                      className="text-xs bg-red-600 text-white px-2 py-0.5 rounded font-bold"
                    >
                      BLOQUEADO · contiene {bloqueoPor}
                    </span>
                  ) : (
                    !l.excluido_paciente && (
                      <div className="flex gap-3 items-center">
                        {l.plato_id != null && comida.paciente_id != null && (
                          <button
                            onClick={() => setSustitutosDe(l)}
                            disabled={loading}
                            className="text-xs text-blue-600 hover:underline"
                            title="Ver sustitutos equivalentes nutricionalmente"
                          >
                            🔄 Sustitutos
                          </button>
                        )}
                        <button
                          onClick={() => toggleExcluir(l)}
                          disabled={loading}
                          className="text-xs text-red-600 hover:underline"
                        >
                          Excluir
                        </button>
                      </div>
                    )
                  )}
                </div>
              </li>
            )
          })}
          {lineasAMostrar.length === 0 && (
            <li className="text-sm text-gray-500 italic text-center py-4">
              Sin platos en esta opción.
            </li>
          )}
        </ul>

        <label className="block text-sm font-semibold mb-1">Notas/comentarios del paciente:</label>
        <textarea
          value={nota}
          onChange={(e) => setNota(e.target.value)}
          rows={3}
          className="w-full p-2 border rounded text-sm"
          placeholder="Ej: 'sin sal', 'cortado en trozos', solicitud especial..."
        />

        {aviso && (
          <p className="mt-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">{aviso}</p>
        )}

        <div className="flex gap-2 mt-4">
          <button
            onClick={onClose}
            className="flex-1 border border-gray-300 py-2 rounded text-sm"
          >
            Cancelar
          </button>
          <button
            onClick={enviar}
            disabled={loading}
            className="flex-1 bg-avante-success text-white py-2 rounded text-sm font-semibold disabled:opacity-50"
          >
            {loading ? 'Enviando...' : '→ Enviar a cocina'}
          </button>
        </div>
      </ModalShell>

      {sustitutosDe && comida.paciente_id != null && sustitutosDe.plato_id != null && (
        <SustitutosModal
          plato_id={sustitutosDe.plato_id}
          plato_nombre={sustitutosDe.plato_nombre ?? 'plato'}
          paciente_id={comida.paciente_id}
          onClose={() => setSustitutosDe(null)}
        />
      )}
    </>
  )
}

// ============================================================
// Modal de sustitutos equivalentes (Fase E - PR #57)
// ============================================================
function SustitutosModal({
  plato_id, plato_nombre, paciente_id, onClose,
}: { plato_id: number; plato_nombre: string; paciente_id: number; onClose: () => void }) {
  const { showToast } = useToast()
  const [sustitutos, setSustitutos] = useState<SustitucionRanked[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    sustitucionesApi.topSustitutos(plato_id, paciente_id)
      .then(setSustitutos)
      .catch((err) => showToast(`Error: ${err.response?.data?.detail || err.message}`, 'error'))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plato_id, paciente_id])

  function copiarAlPortapapeles(texto: string) {
    navigator.clipboard?.writeText(texto)
      .then(() => showToast('Copiado: ' + texto, 'success'))
      .catch(() => showToast('No se pudo copiar', 'error'))
  }

  function labelMotivo(m: string): string {
    if (m === 'PERFIL_NUTRICIONAL_EQUIVALENTE') return 'Perfil nutricional equivalente'
    if (m === 'INGREDIENTE_COMPATIBLE') return 'Ingrediente compatible'
    if (m === 'CULTURAL_EQUIVALENTE') return 'Cultural equivalente'
    return m
  }

  return (
    <ModalShell
      title="Sustitutos para"
      subtitle={`"${plato_nombre}"`}
      onClose={onClose}
      maxWidth={560}
      zIndex={60}
    >
        <p className="text-xs text-gray-500 mb-4">
          Top 5 equivalencias nutricionales compatibles con las alergias del paciente.
        </p>

        {loading && <p className="text-sm">Buscando equivalentes…</p>}
        {!loading && sustitutos.length === 0 && (
          <div className="bg-gray-50 rounded p-4 text-sm text-gray-500 text-center">
            No hay sustitutos cargados para este plato (o todos están filtrados por alergias del paciente).
          </div>
        )}

        <ul className="space-y-2">
          {sustitutos.map((s) => (
            <li key={s.sustitucion_id} className="border rounded p-2 hover:bg-blue-50">
              <div className="flex justify-between items-start mb-1">
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{s.plato_nombre_sustituto}</p>
                  <p className="text-xs text-gray-500 font-mono">{s.plato_codigo_sustituto}</p>
                </div>
                <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded font-bold">
                  {(Number(s.score_equivalencia) * 100).toFixed(0)}%
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-[10px] text-gray-600 italic">{labelMotivo(s.motivo_tipificado)}</span>
                <button onClick={() => copiarAlPortapapeles(s.plato_nombre_sustituto)}
                  className="text-xs text-blue-600 hover:underline">
                  📋 Copiar
                </button>
              </div>
            </li>
          ))}
        </ul>

        <div className="mt-4 flex justify-end">
          <button onClick={onClose}
            className="border border-gray-300 px-4 py-1.5 rounded text-sm">Cerrar</button>
        </div>
    </ModalShell>
  )
}
